import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const NursingOPDAssessmentList = ({ assessments = [], onEdit, onDelete, onAddAssessment }) => {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredAssessments = assessments.filter((a) => {
        const patientName = `${a.patient?.first_name || ""} ${a.patient?.last_name || ""}`.toLowerCase();
        const cnic = (a.patient?.cnic || "").toLowerCase();
        const search = filterText.toLowerCase();
        return patientName.includes(search) || cnic.includes(search);
    });

    const totalRows = filteredAssessments.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
                        {[5, 10, 15, 20, 25].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-1">
                    <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted small">Page {currentPage} of {totalPages}</span>
                    <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "Patient Name",
            selector: (row) => `${row.patient?.first_name || ""} ${row.patient?.last_name || ""}`,
            sortable: true,
            width: "300px"
        },
        {
            name: "CNIC",
            selector: (row) => row.patient?.cnic || "—",
            sortable: true,
            width: "300px"
        },
        { name: "Date", selector: (row) => row.final_date || "—", sortable: true, width: "300px" },
        { name: "Nursing Staff", selector: (row) => row.nursing_name || "—", sortable: true, width: "300px" },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {row.is_referred === 'Yes' && (
                        <button 
                            className="btn btn-sm btn-outline-info" 
                            title="Add Inquiry" 
                            onClick={() => {
                                let programId = "";
                                if (row.referral_service === "Residential Program") programId = "3";
                                else if (row.referral_service === "Day Rehab Program") programId = "1";
                                else if (row.referral_service === "Home Visitation") programId = "2";
                                
                                const queryParam = programId ? `?program_id=${programId}` : "";
                                navigate(`/patients/manage/${row.patient_id}/add-inquiry${queryParam}`);
                            }}
                        >
                            <i className="ri-question-answer-line"></i>
                        </button>
                    )}
                    <button 
                        className="btn btn-sm btn-outline-info" 
                        title="Print Assessment" 
                        onClick={() => window.open(`/nursing-opd-assessments/print/${row.id}`, '_blank')}
                    >
                        <i className="ri-printer-line"></i>
                    </button>
                    {onEdit && (
                        <button className="btn btn-sm btn-outline-primary" title="Edit Assessment" onClick={() => onEdit(row.id)}>
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button className="btn btn-sm btn-outline-danger" title="Delete Assessment" onClick={() => onDelete(row.id)}>
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "180px",
        },
    ];

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Nursing OPD Assessments List", 14, 10);
        autoTable(doc, {
            head: [["Name", "CNIC", "Sex", "Date", "Staff"]],
            body: filteredAssessments.map(a => [
                `${a.patient?.first_name || ""} ${a.patient?.last_name || ""}`,
                a.patient?.cnic || "",
                a.patient?.sex || "",
                a.final_date || "",
                a.nursing_name || ""
            ]),
            startY: 20,
        });
        doc.save("nursing_opd_assessments.pdf");
    };

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 px-0 py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
                            <li><CSVLink data={filteredAssessments} filename="nursing_assessments.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
                        </ul>
                    </div>
                    <input type="text" placeholder="Search assessments..." className="form-control form-control-sm" style={{ width: "200px" }} value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                </div>
                <h6 className="mb-0 text-muted flex-grow-1 text-center">Showing {start}-{end} of {totalRows} assessments</h6>
                {onAddAssessment && (
                    <div>
                        <button className="btn btn-primary btn-sm" onClick={onAddAssessment}>
                            <i className="ri-add-line me-1"></i> Add Assessment
                        </button>
                    </div>
                )}
            </div>
            <div className="card-body p-0">
                <DataTable
                    columns={columns}
                    data={filteredAssessments}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted text-center">No assessments found</div>}
                />
            </div>
        </div>
    );
};

export default NursingOPDAssessmentList;
